/**
 * 근로기준법상 5인 이상 사업장 연장·야간·휴일 가산 상수 및 공휴일 판정
 * 설계문서: required/5인미만/5인이상.md §3.2, §8.4
 *
 * @deprecated 가산률·최저시급·209 등 법령 요율은 Firestore SSOT (`config/rates/{year}/current`) 가 진실.
 * 새 코드는 `useOvertimeWeekdayRate()`, `useMinWageHourly()` 등 `src/hooks/useRates.ts` 의 selector 사용.
 * 아래 export 는 default fallback 으로만 보존 (Firestore 미도달 / 오프라인 보호).
 */

import { useRatesStore } from '../store/ratesStore';

// --- 가산률 (근로기준법 기준) — @deprecated, Firestore `labor.overtime.*` 가 진실 ---
/** 연장 가산률: 1일 8시간 초과분 → 시급의 1.5배 (50% 가산) */
export const OT_PREMIUM_RATE = 0.5;
/** 야간 가산률: 22:00~06:00 → 시급의 1.5배 (50% 가산) */
export const NIGHT_PREMIUM_RATE = 0.5;
/** 휴일 가산률: 휴일 근무 → 시급의 1.5배 (50% 가산) */
export const HOLIDAY_PREMIUM_RATE = 0.5;

// --- 소정 근로시간·야간 시각 ---
/** 1일 소정 근로시간 (시간). 이 초과분에 연장 가산 */
export const STANDARD_WORK_HOURS_PER_DAY = 8;
/** 1일 소정 근로시간 (분) */
export const STANDARD_WORK_MINUTES_PER_DAY = STANDARD_WORK_HOURS_PER_DAY * 60; // 480
/** 야간 시작 시 (22시 포함) */
export const NIGHT_START_HOUR = 22;
/** 야간 종료 시 (다음날 06시 미만까지) */
export const NIGHT_END_HOUR = 6;

/** 유효 근무 분 상한 (48시간 = 2880분) */
export const MAX_WORK_MINUTES_PER_SEGMENT = 48 * 60; // 2880

// --- 최저시급 (연도별, fallback) ---
/**
 * 2026년 최저시급 (원). 직원 hourlyRate 미설정 시 fallback. 설계문서 §2
 * @deprecated Firestore `labor.minWage.hourly` 가 진실. `useMinWageHourly()` 사용.
 */
export const MIN_WAGE_2026 = 10320;

// --- 근로자의 날 (5/1) 관련 ---
/**
 * 근로자의 날 가산률: 5인 이상 출근 시 시급의 0.5배 추가. 설계문서 §3
 * @deprecated Firestore `labor.overtime.laborDay` 가 진실. `useLaborDayPremiumRate()` 사용.
 */
export const LABOR_DAY_PREMIUM_RATE = 0.5;
/**
 * 월급제 → 시급 환산 기준 시간 (40h × 4.345주 + 8h주휴 ≒ 209). 설계문서 §5.1
 * @deprecated Firestore `labor.workhour.monthlyToHourlyDivisor` 가 진실. `useMonthlyToHourlyDivisor()` 사용.
 */
export const MONTHLY_TO_HOURLY_DIVISOR = 209;

// --- 공휴일 default fallback (Firestore `policy.publicHolidays` 미도달 시) ---
// Firestore SSOT 가 진실. 매년 변동 공휴일 추가는 양쪽(Firestore + 이 파일) 동시.
const FIXED_HOLIDAYS: Array<[number, number]> = [
  [1, 1],   // 신정
  [3, 1],   // 삼일절
  [5, 5],   // 어린이날
  [6, 6],   // 현충일
  [8, 15],  // 광복절
  [10, 3],  // 개천절
  [10, 9],  // 한글날
  [12, 25], // 크리스마스
];

const VARIABLE_HOLIDAYS: Record<number, Array<[number, number]>> = {
  2024: [[2, 9], [2, 10], [2, 11], [5, 15], [9, 16], [9, 17], [9, 18]],
  2025: [[1, 28], [1, 29], [1, 30], [5, 5], [10, 5], [10, 6], [10, 7]],
  2026: [[2, 16], [2, 17], [2, 18], [5, 24], [9, 24], [9, 25], [9, 26]],
};

/**
 * 해당 날짜가 공휴일인지 판정 (날짜만 비교, 시간 무시).
 *
 * Firestore `policy.publicHolidays.{fixed,variable}` 우선, 미동기화 시 위 const fallback.
 * 새 코드에서 hook 사용 가능하면 `usePublicHolidaysFor(year)` 권장.
 */
export function isPublicHoliday(date: Date): boolean {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const month = d.getMonth() + 1; // 1-based
  const day = d.getDate();
  const year = d.getFullYear();

  const ph = useRatesStore.getState().current.policy?.publicHolidays;
  const fixed = ph?.fixed ?? FIXED_HOLIDAYS;
  const variable = ph?.variable?.[String(year)] ?? VARIABLE_HOLIDAYS[year];

  for (const [m, dd] of fixed) {
    if (month === m && day === dd) return true;
  }

  if (variable) {
    for (const [m, dd] of variable) {
      if (month === m && day === dd) return true;
    }
  }

  return false;
}
